import { computed, Ref, CSSProperties } from 'vue';
import { DutyItemResponse } from '../types/models/duty';
import { UI_COLORS } from '../constants';
import { useI18n } from 'vue-i18n';
import { useDateTime } from '../../../../components/composables/useDateTime';
import i18n from '@/plugins/i18n';

export interface SelectedIndicator {
	itemKey: string;
	groupKey: 'violations' | 'preferences' | 'absences' | string;
}

export interface BottomSegment {
	color: string;
	striped?: boolean;
	order?: number;
	type: 'preference' | 'absence';
}

const BOTTOM_INDICATOR_MAP: Record<string, Omit<BottomSegment, 'type'>> = {
	pendingHigh: { color: UI_COLORS.ABSENCE_HIGH, striped: true, order: 3 },
	pendingLow: { color: UI_COLORS.ABSENCE_LOW, striped: true, order: 3 },
	met: { color: UI_COLORS.PREF_MET, striped: false, order: 2 },
	notMet: { color: UI_COLORS.PREF_NOT_MET_STRIPED, striped: true, order: 2 },
	partiallyMet: { color: UI_COLORS.PREF_PARTIAL, striped: false, order: 2 },
	approvedHigh: { color: UI_COLORS.ABSENCE_HIGH, striped: false, order: 3 },
	approvedLow: { color: UI_COLORS.ABSENCE_LOW, striped: false, order: 3 },
	preferred_workday_approved: { color: UI_COLORS.PREF_WORK_DAY, striped: false, order: 2 },
	preferred_workday_pending: { color: UI_COLORS.PREF_WORK_DAY, striped: true, order: 2 },
};

const t = (key: string, params?: Record<string, unknown>) => (params ? i18n.global.t(key, params) : i18n.global.t(key));

export function useDutyCell(duty: Ref<DutyItemResponse | null | undefined>, activeIndicators: Ref<SelectedIndicator[] | undefined>) {
	const { formatSecondsDuration, formatTimeToSeconds } = useDateTime();

	const activeDutyFlags = computed(() => {
		const d = duty.value;
		if (!d) return [];

		const indicators = activeIndicators.value || [];
		const groupSet = new Set(indicators.map((i) => i.groupKey));
		if (!groupSet.has('dutyFlags')) return [];

		const keySet = new Set(indicators.map((i) => i.itemKey));

		return [
			keySet.has('internalNotes') && (d.notes || d.internalNotes) ? 'internalNotes' : null,
			keySet.has('fixedDuty') && d.fixed ? 'fixedDuty' : null,
			keySet.has('workReport') && d.hasReports ? 'workReport' : null,
			keySet.has('modifiedDuty') && d.modified ? 'modifiedDuty' : null,
			keySet.has('exchangeDuty') && d.exchange ? 'exchangeDuty' : null,
		].filter(Boolean);
	});

	const tooltip = computed(() => {
		const d = duty.value;
		if (!d) return '';

		// Helper to format numeric time values safely
		const formatTime = (time?: number | string | null): string => {
			if (typeof time === 'number') return formatSecondsDuration(time) || '';
			return time || '';
		};

		// --- GROUP 1: Basic Duty Info ---
		const basicInfoLines: (string | null | undefined)[] = [];

		// Title / Name Header
		const titleName = d.nameKey ? t(d.nameKey) : d.name || (d.type ? t(`duty.${d.type}_short`, t(`duty.${d.type}`, d.type)) : '');
		basicInfoLines.push(titleName);

		// Time Range (Start - End OR Standby)
		const startTime = formatTime(d.start);
		const endTime = formatTime(d.end);
		const standbyStart = formatTime(d.standbyStart);
		const standbyEnd = formatTime(d.standbyEnd);

		if (startTime && endTime) {
			basicInfoLines.push(`${startTime} - ${endTime}`);
		} else if (standbyStart && standbyEnd) {
			basicInfoLines.push(`${t('duty_standby')}: ${standbyStart} - ${standbyEnd}`);
		}

		// Durations (Work Time & Duty Time)
		if (typeof d.workTime === 'number' && d.workTime > 0) {
			basicInfoLines.push(`${t('duty_workTime')}: ${formatSecondsDuration(d.workTime)}`);
		}
		if (typeof d.dutyTime === 'number' && d.dutyTime > 0) {
			basicInfoLines.push(`${t('duty_dutyTime')}: ${formatSecondsDuration(d.dutyTime)}`);
		}

		// Shift
		if (d.shift) {
			basicInfoLines.push(t(`duty_shift_${d.shift}`));
		}

		if (d.internalNotes) {
			basicInfoLines.push(`${t('planning_internal_notes')}: ${d.internalNotes}`);
		}

		if (d.notes) {
			basicInfoLines.push(`${t('duty_template.notes')}: ${d.notes}`);
		}

		// --- GROUP 2: Violations, Met/Unmet Pref, Exchange, Labels ---
		const statusLines: (string | null | undefined)[] = [];

		if (d.hasUnmet) statusLines.push(t('duty_unmet'));
		if (d.hasMet) statusLines.push(t('duty_met'));
		if (d.hasViolations) statusLines.push(t('duty_has_violations'));
		if (d.allViolationsApproved) statusLines.push(t('duty_has_agreement'));
		if (d.exchange) statusLines.push(t('duty_has_exchange'));

		if (Array.isArray(d.labels) && d.labels.length > 0) {
			const validLabels = d.labels.filter((label) => Boolean(label?.name));

			if (validLabels.length > 0) {
				const labelText = validLabels.map((label) => `• ${label.name}`).join('\n');
				statusLines.push(`${t('duty_changed_property_labels')}:\n${labelText}`);
			}
		}
		if (d.affectedByPreview && typeof d.affectedByPreview === 'object') {
			const editors = [...new Set(Object.values(d.affectedByPreview))];

			if (editors.length > 0) {
				const namesText = editors.join(', ');

				statusLines.push(t('duty_protected'));
				statusLines.push(editors.length > 1 ? t('duty_are_editing', { users: namesText }) : t('duty_is_editing', { user: namesText }));
			}
		}

		// Format each group into string blocks
		const basicInfoBlock = basicInfoLines.filter(Boolean).join('\n');
		const statusBlock = statusLines.filter(Boolean).join('\n');

		// Combine groups with a blank line break separator (\n\n)
		return [basicInfoBlock, statusBlock].filter(Boolean).join('\n\n');
	});

	const dutyStyle = computed(() => {
		const d = duty.value;

		if (!d) {
			return {
				classes: {} as Record<string, boolean>,
				name: '',
				tooltip: '',
				styles: {} as CSSProperties,
			};
		}

		let name = d.nameKey ? t(d.nameKey) : d.name;

		if (!name) {
			switch (d.type) {
				case 'absence':
					name = t('duty.absence_short');
					break;
				case 'compensation':
					name = t('duty.compensation_short');
					break;
				case 'rest':
					name = t('duty.rest_short');
					break;
				case 'vacation':
					name = t('duty.vacation_short');
					break;
				case 'reserve':
					name = t('duty.reserve_short');
					break;
				default:
					name = t('duty.default_short');
					break;
			}
		}

		let backgroundColor: string = UI_COLORS.BG_DEFAULT;
		let textColor: string = UI_COLORS.TEXT_DEFAULT;

		switch (d.type) {
			case 'rest':
				backgroundColor = d.isRestSunday ? UI_COLORS.BG_SUNDAY_REST : UI_COLORS.BG_WHITE;
				break;

			case 'compensation':
				backgroundColor = UI_COLORS.BG_WHITE;
				break;

			case 'vacation':
				textColor = UI_COLORS.TEXT_VACATION;
				break;

			case undefined:
				backgroundColor = 'transparent';
				break;

			default:
				backgroundColor = UI_COLORS.BG_DEFAULT;
		}

		return {
			classes: {
				'has-note': activeDutyFlags.value.includes('internalNotes'),
				fixed: activeDutyFlags.value.includes('fixedDuty'),
				exchange: activeDutyFlags.value.includes('exchangeDuty'),
				modified: activeDutyFlags.value.includes('modifiedDuty'),
				'report-indicator': activeDutyFlags.value.includes('workReport'),
			},
			name,
			tooltip: tooltip.value,
			styles: {
				backgroundColor,
				color: textColor,
			} as CSSProperties,
		};
	});

	const topViolationShadow = computed((): CSSProperties => {
		const d = duty.value;
		if (!d || !d.hasViolations) return {};

		const indicators = activeIndicators.value || [];
		const groupSet = new Set(indicators.map((i) => i.groupKey));
		if (!groupSet.has('violations')) return {};

		const keySet = new Set(indicators.map((i) => i.itemKey));

		// A duty can qualify for more than one violation state at once (e.g. an
		// unapproved law violation AND other unapproved violations). Collect the
		// applicable states in priority order, then pick the highest-priority one
		// whose indicator is actually selected — so deselecting the top state falls
		// back to the next selected state instead of showing nothing.
		const candidates: { key: string; color: string }[] = [];

		if (d.allViolationsApproved) {
			candidates.push({ key: 'approvedViolation', color: UI_COLORS.VIOLATION_APPROVED });
		} else {
			if (d.hasUnapprovedLawViolation) {
				candidates.push({ key: 'unapprovedLawViolation', color: UI_COLORS.VIOLATION_CRITICAL });
			}
			candidates.push({ key: 'unapprovedViolation', color: UI_COLORS.VIOLATION_UNAPPROVED });
		}

		const match = candidates.find((c) => keySet.has(c.key));
		const violationColor = match ? match.color : '';

		return violationColor ? { boxShadow: `inset 0 4px 0 0 ${violationColor}` } : {};
	});

	const mergedBottomSegments = computed((): BottomSegment[] => {
		const d = duty.value;
		if (!d) return [];

		const indicators = activeIndicators.value || [];

		const keySet = new Set(indicators.map((i) => i.itemKey));
		const groupSet = new Set(indicators.map((i) => i.groupKey));

		const hasAbsencesActive = groupSet.has('absences');
		const hasPreferencesActive = groupSet.has('preferences');
		const hasWorkdayActive = groupSet.has('preferred_workday');

		let prefSegment: BottomSegment | null = null;
		let absenceSegment: BottomSegment | null = null;

		if (hasPreferencesActive && (d.hasMet || d.hasUnmet)) {
			const key = d.hasMet && d.hasUnmet ? 'partiallyMet' : d.hasMet ? 'hasMet' : 'hasUnmet';

			if (keySet.has(key)) {
				if (key === 'partiallyMet') {
					prefSegment = { color: UI_COLORS.PREF_PARTIAL, striped: false, type: 'preference' };
				} else {
					prefSegment = {
						color: key === 'hasUnmet' ? UI_COLORS.PREF_NOT_MET_STRIPED : UI_COLORS.PREF_MET,
						striped: key === 'hasUnmet',
						type: 'preference',
					};
				}
			}
		}

		if (hasWorkdayActive && d.absenceIsWorkPreference) {
			const key = !d.absenceIsApproved ? 'preferred_workday_pending' : 'preferred_workday_approved';

			if (keySet.has(key)) {
				absenceSegment = {
					...(BOTTOM_INDICATOR_MAP[key] || {
						color: UI_COLORS.PREF_WORK_DAY,
						striped: !d.absenceIsApproved,
					}),
					type: 'absence',
				};
			}
		}

		if (!absenceSegment && hasAbsencesActive && !d.absenceIsWorkPreference) {
			const key =
				d.absencePriority === 2 || d.absencePriority === 3
					? !d.absenceIsApproved
						? 'pendingHigh'
						: 'approvedHigh'
					: d.absencePriority === 1
						? !d.absenceIsApproved
							? 'pendingLow'
							: 'approvedLow'
						: '';

			if (key && keySet.has(key)) {
				absenceSegment = {
					...(BOTTOM_INDICATOR_MAP[key] || {
						color: d.absenceIsApproved ? UI_COLORS.ABSENCE_LEGACY_APPROVED : UI_COLORS.ABSENCE_LEGACY_PENDING,
						striped: !d.absenceIsApproved,
					}),
					type: 'absence',
				};
			}
		}

		const segments: BottomSegment[] = [];
		if (prefSegment) segments.push(prefSegment);
		if (absenceSegment) segments.push(absenceSegment);

		return segments;
	});

	return {
		dutyStyle,
		topViolationShadow,
		mergedBottomSegments,
		activeDutyFlags,
	};
}
